import { useState, useCallback } from 'react';
import { wheelsApi } from '../../../../services/api';
import api from '../../../../services/api';
import { Spin, Wheel } from '../../../../types';
import toast from 'react-hot-toast';

export interface LeadFilters {
  wheel_id?: number | 'manual';
  start_date?: string;
  end_date?: string;
  search?: string;
  won?: boolean;
  is_birthday?: boolean;
  page: number;
  limit: number;
}

export interface Lead {
  id: number;
  customer_name: string;
  customer_birthdate?: string;
  whatsapp: string;
  wheel_name: string;
  wheel_id: number;
  product_name?: string;
  product_id?: number;
  product_ids?: number[];  // Múltiplos produtos em sorteios agendados
  product_names?: string[];  // Nomes dos múltiplos produtos
  product_images?: string[];  // Imagens dos múltiplos produtos
  custom_data?: Record<string, any>;
  custom_fields?: any[];
  created_at: string;
  won: boolean;
}

export interface LeadsStats {
  total_leads: number;
  unique_leads: number;
  conversion_rate: number;
  leads_with_prizes: number;
}

export const useLeadsData = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<LeadFilters>({
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [stats, setStats] = useState<LeadsStats>({
    total_leads: 0,
    unique_leads: 0,
    conversion_rate: 0,
    leads_with_prizes: 0
  });

  const calculateStats = useCallback((leadsData: Lead[]) => {
    const totalLeads = leadsData.length;
    const uniqueWhatsApps = new Set(leadsData.map(lead => lead.whatsapp)).size;
    const leadsWithPrizes = leadsData.filter(lead => lead.won).length;
    const conversionRate = totalLeads > 0 ? (leadsWithPrizes / totalLeads) * 100 : 0;

    setStats({
      total_leads: totalLeads,
      unique_leads: uniqueWhatsApps,
      conversion_rate: Math.round(conversionRate * 100) / 100,
      leads_with_prizes: leadsWithPrizes
    });
  }, []);

  const loadWheels = useCallback(async () => {
    try {
      const response = await wheelsApi.getAll({
        page: 1,
        limit: 1000
      });
      setWheels(response.wheels);
    } catch (error: any) {
      console.error('Erro ao carregar roda de prêmios:', error);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);

      const allLeads: Lead[] = [];

      // Se filtro de aniversariantes está ativo
      if (filters.is_birthday) {
        const response = await api.get('/wheels/spins/birthdays', {
          params: {
            page: filters.page,
            limit: filters.limit
          }
        });

        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }

        const leadsData = (response.data.spins || []).map((spin: any) => ({
          id: spin.id,
          customer_name: spin.customer_name,
          customer_birthdate: spin.customer_birthdate,
          whatsapp: spin.whatsapp,
          wheel_name: spin.wheel_name || 'Sorteio Manual',
          wheel_id: spin.wheel_id,
          product_name: spin.prize_name,
          product_id: spin.product_id,
          product_ids: spin.product_ids,
          product_names: spin.product_names,
          product_images: spin.product_images,
          custom_data: spin.custom_data,
          custom_fields: wheels.find(w => w.id === spin.wheel_id)?.custom_fields || [],
          created_at: spin.created_at,
          won: spin.won
        }));

        allLeads.push(...leadsData);
      } else if (filters.wheel_id) {
        // Se uma roda específica foi selecionada
        const response = await wheelsApi.getSpins(filters.wheel_id, {
          page: filters.page,
          limit: filters.limit
        });

        if (response.pagination) {
          setPagination(response.pagination);
        }

        const wheel = wheels.find(w => w.id === filters.wheel_id);
        const wheelName = wheel?.name || 'Sorteio Manual';

        const leadsData = (response.spins || []).map((spin: Spin) => ({
          id: spin.id,
          customer_name: spin.customer_name,
          customer_birthdate: spin.customer_birthdate,
          whatsapp: spin.whatsapp,
          wheel_name: wheelName,
          wheel_id: spin.wheel_id,
          product_name: spin.product_name,
          product_id: spin.product_id,
          product_ids: spin.product_ids,
          product_names: spin.product_names,
          product_images: spin.product_images,
          custom_data: spin.custom_data,
          custom_fields: wheel?.custom_fields || [],
          created_at: spin.created_at,
          won: spin.won
        }));

        allLeads.push(...leadsData);
      } else {
        try {
          const response = await wheelsApi.getAllSpins(filters);

          const leadsData = (response.spins || []).map((spin: Spin) => ({
            id: spin.id,
            customer_name: spin.customer_name,
            customer_birthdate: spin.customer_birthdate,
            whatsapp: spin.whatsapp,
            wheel_name: spin.wheel_name || 'Sorteio Manual',
            wheel_id: spin.wheel_id,
            product_name: spin.product_name,
            product_id: spin.product_id,
            product_ids: spin.product_ids,
            product_names: spin.product_names,
            product_images: spin.product_images,
            custom_data: spin.custom_data,
            custom_fields: wheels.find(w => w.id === spin.wheel_id)?.custom_fields || [],
            created_at: spin.created_at,
            won: spin.won
          }));

          allLeads.push(...leadsData);

          setPagination(response?.pagination);
        } catch (error) {
          console.error('Erro ao carregar spins de todas as roda de prêmios:', error);
        }
      }

      // Aplicar filtros locais
      let filteredLeads = allLeads;

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredLeads = filteredLeads.filter(lead =>
          lead.customer_name.toLowerCase().includes(searchLower) ||
          lead.whatsapp.includes(filters.search!) ||
          lead.wheel_name.toLowerCase().includes(searchLower)
        );
      }

      if (filters.won !== undefined) {
        filteredLeads = filteredLeads.filter(lead => lead.won === filters.won);
      }

      if (filters.start_date) {
        filteredLeads = filteredLeads.filter(lead =>
          new Date(lead.created_at) >= new Date(filters.start_date!)
        );
      }

      if (filters.end_date) {
        filteredLeads = filteredLeads.filter(lead =>
          new Date(lead.created_at) <= new Date(filters.end_date!)
        );
      }

      // Ordenar por data mais recente
      filteredLeads.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setLeads(filteredLeads);
      calculateStats(filteredLeads);

    } catch (error: any) {
      toast.error('Erro ao carregar leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, wheels, calculateStats]);

  const updateFilter = useCallback((field: keyof LeadFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ page: 1, limit: 20 });
  }, []);

  return {
    leads,
    wheels,
    loading,
    filters,
    pagination,
    stats,
    loadWheels,
    loadLeads,
    updateFilter,
    clearFilters
  };
};
